/**
 * useAgentDirectory — speed dials and transfer targets for the softphone.
 *
 * THE DEFECT
 * ----------
 * `speed_dial` and `transfer` are per-tenant tables with full CRUD, admin
 * pages and permissions, and no reader outside those admin routes. An
 * operator curated a directory that no agent could see.
 *
 * What the agent got instead: a FREE-TEXT BOX for transfers, typed from
 * memory while a customer waits, and no speed dial at all — while
 * `CompanyDirectoryPage.tsx:184` displayed extensions as non-clickable text.
 *
 * Loaded once per mount and cached in module scope for the session: a
 * directory changes on the order of days, and re-fetching it every time an
 * agent opens the transfer panel puts a request in front of a live call.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet } from '../api/client.js'

export interface DirectoryEntry {
  id: string
  name: string
  number: string
  source: 'speed_dial' | 'transfer' | 'agent'
  /** Agents only — so the UI can avoid offering a transfer to someone offline. */
  status?: string
}

interface DirectoryResponse {
  items: DirectoryEntry[]
}

/** Session cache. Cleared on reload, which is when a re-fetch is cheap. */
const cache = new Map<string, DirectoryEntry[]>()

function useDirectory(path: string): {
  items: DirectoryEntry[]
  loading: boolean
  error: string | null
  refresh: () => void
} {
  const [items, setItems] = useState<DirectoryEntry[]>(() => cache.get(path) ?? [])
  const [loading, setLoading] = useState(!cache.has(path))
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (force: boolean) => {
      if (!force && cache.has(path)) return
      setLoading(true)
      setError(null)
      try {
        const res = await apiGet<DirectoryResponse>(path)
        const next = res?.items ?? []
        cache.set(path, next)
        setItems(next)
      } catch (err) {
        // Surfaced rather than swallowed: an empty directory and a failed
        // fetch look identical to an agent, and one of them is worth
        // retrying.
        setError(err instanceof Error ? err.message : 'Could not load directory')
      } finally {
        setLoading(false)
      }
    },
    [path],
  )

  useEffect(() => {
    void load(false)
  }, [load])

  const refresh = useCallback(() => {
    cache.delete(path)
    void load(true)
  }, [load, path])

  return { items, loading, error, refresh }
}

/** Curated numbers for the dial pad. */
export function useSpeedDials() {
  return useDirectory('/agent/speed-dials')
}

/**
 * Transfer targets: curated `transfer` rows PLUS colleagues with extensions.
 *
 * Merged server-side so the UI does not have to reconcile two lists mid-call.
 */
export function useTransferTargets() {
  return useDirectory('/agent/transfer-targets')
}

/* ─────────────────────────────────────────────────────────────────────────
 * Transfer drawer — grouped routing destinations.
 * ─────────────────────────────────────────────────────────────────────── */

/** One row in a drawer section. `dialable` marks rows a conference
 *  (attended) transfer can originate a leg to — a queue or IVR has no
 *  endpoint, so it is blind-only. */
export interface TransferDestinationRow {
  id: string
  name: string
  /** Present for dialable rows: extension or phone number. */
  number?: string
  /** Users only: live presence. */
  status?: string
  /** Queues only: `queues` or `fifo_queues` — the dest: module name. */
  table?: string
  dialable: boolean
}

export interface TransferDestinations {
  directTransfers: TransferDestinationRow[]
  users: TransferDestinationRow[]
  ivr: TransferDestinationRow[]
  csatIvr: TransferDestinationRow[]
  callCenters: TransferDestinationRow[]
  queues: TransferDestinationRow[]
  ringGroups: TransferDestinationRow[]
  playNGo: TransferDestinationRow[]
}

const destCache = new Map<string, TransferDestinations>()

/** Grouped transfer destinations for the softphone's transfer drawer. */
export function useTransferDestinations(): {
  groups: TransferDestinations | null
  loading: boolean
  error: string | null
  refresh: () => void
} {
  const path = '/agent/transfer-destinations'
  const [groups, setGroups] = useState<TransferDestinations | null>(() => destCache.get(path) ?? null)
  const [loading, setLoading] = useState(!destCache.has(path))
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (force: boolean) => {
    if (!force && destCache.has(path)) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiGet<TransferDestinations>(path)
      const next = res ?? null
      if (next) destCache.set(path, next)
      setGroups(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load transfer destinations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(false)
  }, [load])

  const refresh = useCallback(() => {
    destCache.delete(path)
    void load(true)
  }, [load])

  return { groups, loading, error, refresh }
}
